import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { todoService } from '../../services';
import { Loader, Select, Button } from '../../components';
import { 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  MoreVertical, 
  Calendar, 
  Trash2, 
  Edit2,
  ChevronRight,
  User as UserIcon,
  Flag
} from 'lucide-react';
import toast from 'react-hot-toast';

const TodoList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTodo, setCurrentTodo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const todoRes = await todoService.getAllTodos();

      if (todoRes.success) setTodos(todoRes.data || []);
      
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load todos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (todo = null) => {
    if (todo) {
      setIsEditing(true);
      setCurrentTodo(todo);
      setFormData({
        title: todo.title,
        description: todo.description || '',
        due_date: todo.due_date || '',
        priority: todo.priority || 'medium',
        status: todo.status || 'pending'
      });
    } else {
      setIsEditing(false);
      setCurrentTodo(null);
      setFormData({
        title: '',
        description: '',
        due_date: new Date().toISOString().split('T')[0],
        priority: 'medium',
        status: 'pending'
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentTodo(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (isEditing && currentTodo) {
        const res = await todoService.updateTodo(currentTodo.id, formData);
        if (res.success) {
          toast.success('Task updated successfully');
          fetchData();
          handleCloseModal();
        }
      } else {
        const res = await todoService.createTodo(formData);
        if (res.success) {
          toast.success('Task created successfully');
          fetchData();
          handleCloseModal();
        }
      }
    } catch (err) {
      toast.error(err.message || 'Error saving task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setSubmitting(true);
      const res = await todoService.deleteTodo(deleteConfirm.id);
      if (res.success) {
        toast.success('Task deleted successfully');
        setTodos(prev => prev.filter(t => t.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      }
    } catch (err) {
      toast.error(err.message || 'Error deleting task');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (todo) => {
    try {
      const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
      const res = await todoService.updateTodo(todo.id, { ...todo, status: newStatus });
      if (res.success) {
        setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, status: newStatus } : t));
        toast.success(`Marked as ${newStatus}`);
      }
    } catch (err) {
      toast.error(err.message || 'Error updating status');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900/50';
      case 'medium': return 'bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50';
      case 'low': return 'bg-green-100 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900/50';
      default: return 'bg-gray-100 dark:bg-[#31373f] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  // Countdown to the due date: days remaining (or overdue), with a colour that
  // shifts green → amber → red as the deadline approaches / passes.
  const getDaysLeft = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
    if (Number.isNaN(due.getTime())) return null;
    const diff = Math.round((due - today) / 86400000);
    const green = 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400';
    const amber = 'bg-amber-50 text-amber-600 dark:bg-amber-900/10 dark:text-amber-400';
    const red = 'bg-rose-50 text-rose-600 dark:bg-red-900/10 dark:text-red-400';
    if (diff > 3) return { label: `${diff} days left`, cls: green };
    if (diff > 1) return { label: `${diff} days left`, cls: amber };
    if (diff === 1) return { label: 'Due tomorrow', cls: amber };
    if (diff === 0) return { label: 'Due today', cls: amber };
    if (diff === -1) return { label: '1 day overdue', cls: red };
    return { label: `${Math.abs(diff)} days overdue`, cls: red };
  };

  const filteredTodos = todos.filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) || 
                         (t.description && t.description.toLowerCase().includes(search.toLowerCase()));
    const matchesFilter = filterStatus === 'all' || t.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) return <Loader text="Loading tasks..." />;

  return (
    <div className="space-y-6 pb-12 font-sans">
      {/* Header Area */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-[#1E293B] dark:text-gray-100 tracking-tight">Organization To-Do</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-0.5">Manage tasks and operations for your facility</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-[#2563EB] text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition shadow-sm shadow-[#2563EB]/20 text-[14px] font-medium shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Task
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-[#262b33] rounded-2xl shadow-sm border border-[#E2E8F0] dark:border-gray-800 p-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <Select
            value={filterStatus}
            onChange={(v) => setFilterStatus(v)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' },
            ]}
            leftIcon={<Filter className="w-4 h-4" />}
            className="w-full sm:w-[150px]"
          />
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 text-[13px] dark:text-gray-200 transition-colors bg-[#F8FAFC] dark:bg-[#31373f]"
            />
          </div>
        </div>

        <div className="flex items-center gap-4 text-[13px] font-medium text-[#64748B] dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            Total: {todos.length}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            Pending: {todos.filter(t => t.status !== 'completed').length}
          </span>
        </div>
      </div>

      {/* List Layout (Horizontal Items) */}
      <div className="flex flex-col gap-3">
        {filteredTodos.length > 0 ? (
          filteredTodos.map((todo) => (
            <div
              key={todo.id}
              className={`group bg-white dark:bg-[#262b33] rounded-xl px-5 py-4 shadow-sm border border-[#E2E8F0] dark:border-gray-800 transition-all flex items-center gap-4 ${
                todo.status === 'completed' ? 'opacity-60 bg-[#F8FAFC] dark:bg-[#20242c]' : 'hover:shadow-md'
              }`}
            >
              {/* Status Toggle (Left) */}
              <button
                onClick={() => toggleStatus(todo)}
                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  todo.status === 'completed' 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : 'border-[#CBD5E1] hover:border-green-500 text-transparent'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
              </button>

              {/* Task Content (Center-Left) */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-0.5">
                  <h3 className={`text-[15px] font-bold text-[#1E293B] dark:text-gray-100 truncate transition-all ${
                    todo.status === 'completed' ? 'line-through text-[#94A3B8]' : ''
                  }`}>
                    {todo.title}
                  </h3>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getPriorityColor(todo.priority)}`}>
                    {todo.priority}
                  </span>
                  {/* Days-left countdown (hidden once the task is done) */}
                  {todo.status !== 'completed' && (() => {
                    const d = getDaysLeft(todo.due_date);
                    return d ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${d.cls}`}>
                        <Clock className="w-2.5 h-2.5" />
                        {d.label}
                      </span>
                    ) : null;
                  })()}
                </div>
                <p className={`text-[13px] text-[#64748B] dark:text-gray-400 truncate transition-all ${
                  todo.status === 'completed' ? 'line-through opacity-70' : ''
                }`}>
                  {todo.description || 'No description provided.'}
                </p>
              </div>

              {/* Metadata (Center-Right) */}
              <div className="hidden md:flex items-center gap-4 flex-shrink-0 px-4 border-l border-[#F1F5F9] dark:border-gray-800">
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-1.5 text-[#64748B] dark:text-gray-400">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="text-[12px] font-medium">
                      {todo.due_date ? 
                        new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(new Date(todo.due_date)) 
                        : 'No Date'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-medium text-[#94A3B8] dark:text-gray-500 capitalize mt-0.5">
                    <Clock className="w-3 h-3" />
                    {todo.status.replace('_', ' ')}
                  </div>
                </div>
              </div>

              {/* Actions (Right) */}
              <div className="flex items-center gap-1 flex-shrink-0 pl-4 border-l border-[#F1F5F9] dark:border-gray-800">
                <button 
                  onClick={() => handleOpenModal(todo)}
                  disabled={todo.status === 'completed'}
                  className={`p-2 rounded-lg transition-colors ${
                    todo.status === 'completed' 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-blue-600 hover:bg-blue-50'
                  }`}
                  title={todo.status === 'completed' ? 'Cannot edit completed task' : 'Edit'}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setDeleteConfirm(todo)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="py-16 flex flex-col items-center justify-center bg-white dark:bg-[#262b33] rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
            <AlertCircle className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 font-medium text-[15px]">No tasks found matches your filters.</p>
          </div>
        )}
      </div>

      {/* Modern Modal (Create/Edit) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseModal}></div>
          <div className="relative w-full max-w-lg bg-white dark:bg-[#262b33] rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[#E2E8F0] dark:border-gray-800 flex justify-between items-center bg-[#F8FAFC] dark:bg-[#20242c]">
              <h2 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-100">
                {isEditing ? 'Edit Task' : 'Create New Task'}
              </h2>
              <button onClick={handleCloseModal} className="p-1 hover:bg-[#E2E8F0] dark:hover:bg-[#31373f] rounded-lg transition-colors">
                <ChevronRight className="w-5 h-5 text-[#64748B] dark:text-gray-400 rotate-90" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5">Task Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. Monthly Safety Check"
                  className="w-full px-4 py-2.5 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[14px]"
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="3"
                  placeholder="Additional details..."
                  className="w-full px-4 py-2.5 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[14px] resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5">Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2.5 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[14px]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5">Priority</label>
                  <Select
                    value={formData.priority}
                    onChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                    ]}
                    className="w-full"
                  />
                </div>
              </div>


              {isEditing && (
                <div>
                  <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5">Status</label>
                  <Select
                    value={formData.status}
                    onChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                    className="w-full"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                  disabled={submitting || !formData.title}
                  className="flex-1"
                >
                  {submitting ? (isEditing ? 'Updating…' : 'Saving…') : (isEditing ? 'Update Task' : 'Save Task')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans">
          <div className="bg-white dark:bg-[#262b33] rounded-[16px] shadow-xl p-6 max-w-[400px] w-full animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mb-4 border border-red-100 dark:border-red-900/50">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">Delete Task</h3>
            <p className="text-[14px] text-[#475569] dark:text-gray-300 mb-6 leading-relaxed">
              Are you sure you want to delete <strong>{deleteConfirm.title}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={submitting}
                disabled={submitting}
              >
                {submitting ? 'Deleting…' : 'Delete Task'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoList;
